import { useEffect, useState, type ReactNode } from 'react';
import { HTTPError } from 'ky';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Profile } from '@bookclub/shared';
import { requestWebEmailOtp, verifyWebEmailOtp } from '@/features/auth/auth.api';
import { formatOtpFlowError } from '@/features/auth/error-format';
import { useAuthStore } from '@/features/auth/store';
import { fetchMe, linkProfileRecoveryEmail, updateProfile } from '@/features/profile/profile.api';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
  updateNotificationTimezone,
} from '@/features/notifications/notifications.api';
import {
  getRuntimeTimezone,
  requestProfileWebPushSubscription,
} from '@/features/notifications/web-push';
import { clearStoredWebSession } from '@/features/auth/web-session-storage';
import { trackEvent } from '@/shared/lib/analytics';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { SectionHeader } from '@/shared/components/ui/SectionHeader';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { SettingsRow } from '@/shared/components/profile/SettingsRow';
import { ProfileHero } from '@/shared/components/profile/ProfileHero';
import { buildLegalHref } from '@/features/legal/legal-navigation';

const SUPPORT_USERNAME = import.meta.env['VITE_SUPPORT_USERNAME'] ?? 'inhoundsupport';
const SUPPORT_EMAIL = import.meta.env['VITE_SUPPORT_EMAIL'] ?? 'info@inhound.club';
const EMAIL_CONFLICT_SUPPORT_MESSAGE =
  'Этот email уже привязан к другому пользователю. Используйте другой email или обратитесь в поддержку: @inhoundsupport или info@inhound.club.';

type ActivePanel = 'edit-name' | 'support' | 'email-link' | null;

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function getVerifiedEmail(profile: Profile) {
  return (
    profile.primaryEmail ??
    profile.identities.find(
      (identity) =>
        identity.provider === 'supabase_email' && identity.verifiedAt && identity.emailNormalized,
    )?.emailNormalized ??
    null
  );
}

function isTelegramBacked(profile: Profile) {
  return Boolean(
    profile.telegramUserId ||
    profile.identities.some((identity) => identity.provider === 'telegram'),
  );
}

function getSupportUrl(message?: string) {
  const base = `https://t.me/${SUPPORT_USERNAME}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

function getSupportEmailUrl(subject?: string) {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  const query = params.toString();
  return `mailto:${SUPPORT_EMAIL}${query ? `?${query}` : ''}`;
}

function EmailConflictSupportMessage() {
  const supportTelegramUrl = getSupportUrl();
  const supportEmailUrl = getSupportEmailUrl();

  return (
    <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
      Этот email уже привязан к другому пользователю. Используйте другой email или обратитесь в
      поддержку:{' '}
      <a
        href={supportTelegramUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.preventDefault();
          openExternal(supportTelegramUrl);
        }}
        className="font-semibold underline decoration-[rgba(123,49,21,0.32)] underline-offset-2"
      >
        @inhoundsupport
      </a>{' '}
      или{' '}
      <a
        href={supportEmailUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.preventDefault();
          openExternal(supportEmailUrl);
        }}
        className="font-semibold underline decoration-[rgba(123,49,21,0.32)] underline-offset-2"
      >
        info@inhound.club
      </a>
      .
    </p>
  );
}

function openExternal(url: string) {
  if (typeof window === 'undefined') return;
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.assign(url);
  }
}

function navigateTo(href: string) {
  if (typeof window === 'undefined') return;
  window.location.assign(href);
}

function ProfileTitle() {
  return (
    <div className="space-y-1">
      <h1 className="text-[28px] font-black uppercase leading-[0.9] tracking-[0.01em] text-[var(--foreground)] min-[430px]:text-[32px] min-[430px]:tracking-[0.02em]">
        ПРОФИЛЬ
      </h1>
    </div>
  );
}

function ProfileLoadingState() {
  return (
    <PageFrame container="readable-content" className="space-y-4">
      <ProfileTitle />
      <MiniappSurface className="overflow-hidden p-0">
        <div className="flex items-start gap-3 p-4 min-[430px]:p-5">
          <div className="h-16 w-16 shrink-0 animate-pulse rounded-full bg-[rgba(26,26,26,0.08)] min-[430px]:h-[72px] min-[430px]:w-[72px]" />
          <div className="min-w-0 flex-1 space-y-2 pt-1">
            <div className="h-4 w-2/3 animate-pulse rounded-full bg-[rgba(26,26,26,0.08)]" />
            <div className="h-3 w-1/2 animate-pulse rounded-full bg-[rgba(26,26,26,0.08)]" />
            <div className="h-5 w-28 animate-pulse rounded-full bg-[rgba(107,78,230,0.12)]" />
          </div>
        </div>
      </MiniappSurface>
      <NeutralLoadingState rows={3} ariaLabel="Загрузка профиля" />
    </PageFrame>
  );
}

function ProfileSignedOutState({ onOpenAuth }: { onOpenAuth: () => void }) {
  return (
    <PageFrame container="readable-content" className="space-y-4">
      <ProfileTitle />
      <MiniappSurface className="space-y-4 px-5 py-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(107,78,230,0.12)] text-2xl">
          ✉️
        </div>
        <div className="space-y-2">
          <h2 className="text-[18px] font-black uppercase tracking-[0.02em] text-[var(--foreground)]">
            Войти / Зарегистрироваться
          </h2>
          <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.58)]">
            Войдите по email, чтобы открыть покупки, продолжить чтение и синхронизировать профиль.
          </p>
        </div>
        <PrimaryButton onClick={onOpenAuth} className="w-full">
          Войти / Зарегистрироваться
        </PrimaryButton>
      </MiniappSurface>
    </PageFrame>
  );
}

function ProfileRestrictedState({
  onOpenCatalog,
  onOpenSupport,
}: {
  onOpenCatalog: () => void;
  onOpenSupport: () => void;
}) {
  return (
    <PageFrame container="readable-content" className="space-y-4">
      <ProfileTitle />
      <MiniappSurface className="space-y-4 px-5 py-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(232,93,42,0.12)] text-2xl">
          ⚠️
        </div>
        <div className="space-y-2">
          <h2 className="text-[18px] font-black uppercase tracking-[0.02em] text-[var(--foreground)]">
            Профиль недоступен
          </h2>
          <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.58)]">
            Перезапустите приложение из Telegram, чтобы увидеть аккаунт, доступы и настройки.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <PrimaryButton onClick={onOpenCatalog} className="w-full">
            Открыть каталог
          </PrimaryButton>
          <PrimaryButton onClick={onOpenSupport} tone="ghost" className="w-full">
            Написать в поддержку
          </PrimaryButton>
        </div>
      </MiniappSurface>
    </PageFrame>
  );
}

function getDisplayName(profile: Profile) {
  return (
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.username ||
    profile.primaryEmail ||
    'Пользователь'
  );
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const runtime = useAuthStore((s) => s.runtime);
  const setSignedOut = useAuthStore((s) => s.setSignedOut);
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [emailLinkEmail, setEmailLinkEmail] = useState('');
  const [emailLinkCode, setEmailLinkCode] = useState('');
  const [emailLinkStep, setEmailLinkStep] = useState<'request' | 'verify'>('request');
  const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
  const [emailLinkSentTo, setEmailLinkSentTo] = useState('');
  const [emailLinkPending, setEmailLinkPending] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);

  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: isAuthenticated,
  });

  const { data: notificationPrefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
    enabled: isAuthenticated,
  });

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['me'], data);
      useAuthStore.setState({ user: data });
      setActivePanel(null);
      setDisplayNameError(null);
    },
  });

  const notificationMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
      setNotificationError(null);
    },
    onError: () => {
      setNotificationError('Не удалось сохранить настройки уведомлений. Попробуйте ещё раз.');
    },
  });

  useEffect(() => {
    trackEvent('profile_open');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const timezone = getRuntimeTimezone();
    if (!timezone) return;
    void updateNotificationTimezone({ timezone }).then((prefs) => {
      queryClient.setQueryData(['notification-preferences'], prefs);
    }).catch(() => undefined);
  }, [isAuthenticated, queryClient]);

  const restricted = isAuthenticated && !isLoading && !me;
  const profile = me ?? null;
  const verifiedEmail = profile ? getVerifiedEmail(profile) : null;
  const showEmailLinkCta = profile ? isTelegramBacked(profile) && !verifiedEmail : false;

  useEffect(() => {
    if (!profile || activePanel !== 'edit-name') return;
    setDisplayNameDraft(getDisplayName(profile));
    setDisplayNameError(null);
  }, [activePanel, profile]);

  useEffect(() => {
    if (!profile || activePanel !== 'email-link') return;
    setEmailLinkError(null);
  }, [activePanel, profile]);

  if (!isAuthenticated) {
    return <ProfileSignedOutState onOpenAuth={() => navigateTo('/auth')} />;
  }

  if (isLoading) {
    return <ProfileLoadingState />;
  }

  if (restricted) {
    return (
      <ProfileRestrictedState
        onOpenCatalog={() => void window.location.assign('/catalog')}
        onOpenSupport={() => {
          trackEvent('profile_support_click', { source: 'restricted' });
          openExternal(getSupportUrl());
        }}
      />
    );
  }

  if (!profile) {
    return null;
  }

  function openSupportSurface() {
    setActivePanel('support');
    trackEvent('profile_support_click', { source: 'profile' });
    trackEvent('profile_support_open');
  }

  function openNameEditSurface() {
    setActivePanel('edit-name');
    trackEvent('profile_name_edit_open');
  }

  function openEmailLinkSurface() {
    resetEmailLinkState();
    setActivePanel('email-link');
    trackEvent('profile_email_link_open');
  }

  function resetEmailLinkState() {
    setEmailLinkEmail('');
    setEmailLinkCode('');
    setEmailLinkStep('request');
    setEmailLinkError(null);
    setEmailLinkSentTo('');
    setEmailLinkPending(false);
  }

  function handleSaveDisplayName() {
    const normalized = displayNameDraft.trim().replace(/\s+/g, ' ');
    if (!normalized) {
      setDisplayNameError('Введите имя, которое будет видно в профиле.');
      return;
    }
    profileMutation.mutate({ displayName: normalized });
    trackEvent('profile_name_edit_save', { source: 'profile' });
  }

  async function handleNotificationToggle(
    key: 'broadcastConsent' | 'personalConsent',
    value: boolean,
  ) {
    try {
      await notificationMutation.mutateAsync({ [key]: value });
      trackEvent('profile_notification_toggle', { key, value });
    } catch {
      return;
    }

    if (key === 'personalConsent' && value) {
      const result = await requestProfileWebPushSubscription();
      trackEvent('profile_web_push_permission_result', { result });
    }
  }

  function handleLogout() {
    setActivePanel(null);
    queryClient.removeQueries({ queryKey: ['me'] });
    clearStoredWebSession();
    setSignedOut(runtime);
  }

  async function handleRequestEmailLinkCode() {
    const normalizedEmail = normalizeEmail(emailLinkEmail);
    if (!normalizedEmail) {
      setEmailLinkError('Введите корректный email, например name@example.com.');
      return;
    }

    setEmailLinkError(null);
    setEmailLinkPending(true);

    try {
      await requestWebEmailOtp(normalizedEmail);
      setEmailLinkSentTo(normalizedEmail);
      setEmailLinkStep('verify');
      setEmailLinkCode('');
      trackEvent('profile_email_link_request');
    } catch (error) {
      setEmailLinkError(
        await formatOtpFlowError(error, {
          invalidEmail: 'Введите корректный email, например name@example.com.',
          invalidCode: 'Неверный код. Проверьте письмо и попробуйте ещё раз.',
          expiredCode: 'Код истёк. Запросите новый одноразовый код.',
          unavailable: 'Сервис добавления email временно недоступен. Попробуйте ещё раз.',
          generic: 'Не удалось добавить email. Попробуйте ещё раз.',
        }),
      );
    } finally {
      setEmailLinkPending(false);
    }
  }

  async function handleVerifyEmailLinkCode() {
    if (!emailLinkSentTo || !emailLinkCode.trim()) {
      setEmailLinkError('Введите код из письма.');
      return;
    }

    setEmailLinkError(null);
    setEmailLinkPending(true);

    try {
      const verification = await verifyWebEmailOtp(emailLinkSentTo, emailLinkCode.trim());
      trackEvent('profile_email_link_verify');
      const result = await linkProfileRecoveryEmail({
        accessToken: verification.supabaseAccessToken,
      });

      queryClient.setQueryData(['me'], result.profile);
      queryClient.setQueryData(['profile-access'], result.access);
      useAuthStore.setState({ user: result.profile });
      resetEmailLinkState();
      setActivePanel(null);
      trackEvent('profile_email_link_success');
    } catch (error) {
      if (error instanceof HTTPError && error.response.status === 409) {
        setEmailLinkError(EMAIL_CONFLICT_SUPPORT_MESSAGE);
        trackEvent('profile_email_link_conflict');
        return;
      }

      setEmailLinkError(
        await formatOtpFlowError(error, {
          invalidEmail: 'Введите корректный email, например name@example.com.',
          invalidCode: 'Неверный код. Проверьте письмо и попробуйте ещё раз.',
          expiredCode: 'Код истёк. Запросите новый одноразовый код.',
          unavailable: 'Сервис добавления email временно недоступен. Попробуйте ещё раз.',
          generic: 'Не удалось добавить email. Попробуйте ещё раз.',
        }),
      );
    } finally {
      setEmailLinkPending(false);
    }
  }

  const legalHref = buildLegalHref('/legal', '/profile');
  const supportUrl = getSupportUrl();
  const supportEmailUrl = getSupportEmailUrl();

  return (
    <>
      <PageFrame container="readable-content" className="space-y-4">
        <ProfileTitle />

        <ProfileHero profile={profile} onEditName={openNameEditSurface} />

        {showEmailLinkCta || verifiedEmail ? (
          <MiniappSurface className="space-y-3 px-5 py-5">
            {showEmailLinkCta ? (
              <>
                <div className="space-y-1">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
                    Email аккаунта
                  </p>
                  <p className="text-[13px] leading-5 text-[rgba(26,26,26,0.58)]">
                    Профиль создан через Telegram и пока не имеет подтверждённого email. Добавьте
                    email, чтобы привязать его к аккаунту.
                  </p>
                </div>
                <PrimaryButton onClick={openEmailLinkSurface} className="w-full">
                  Добавить email
                </PrimaryButton>
              </>
            ) : (
              <div className="space-y-1">
                <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
                  Email аккаунта
                </p>
                <p className="text-[14px] font-semibold text-[var(--foreground)]">
                  {verifiedEmail}
                </p>
              </div>
            )}
          </MiniappSurface>
        ) : null}

        <div className="space-y-2">
          <SectionHeader label="УВЕДОМЛЕНИЯ" />
          <MiniappSurface className="overflow-hidden p-0">
            <ConsentToggleRow
              label="Информация о новых клубах и акциях"
              description="Новые клубы, акции и важные новости без спама."
              checked={notificationPrefs?.broadcastConsent ?? false}
              disabled={notificationMutation.isPending}
              onChange={(checked) => void handleNotificationToggle('broadcastConsent', checked)}
            />
            <div className="border-t border-[rgba(26,26,26,0.06)]" />
            <ConsentToggleRow
              label="Информация о прогрессе чтения"
              description="Напоминания о чтении и недельная сводка."
              checked={notificationPrefs?.personalConsent ?? true}
              disabled={notificationMutation.isPending}
              onChange={(checked) => void handleNotificationToggle('personalConsent', checked)}
            />
          </MiniappSurface>
          {notificationError ? (
            <p className="px-1 text-[12px] leading-5 text-[rgba(232,93,42,0.9)]">
              {notificationError}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <SectionHeader label="ПОМОЩЬ И ДОКУМЕНТЫ" />
          <MiniappSurface className="overflow-hidden p-0">
            <SettingsRow
              label="Поддержка"
              description={`Telegram @${SUPPORT_USERNAME}`}
              onClick={openSupportSurface}
              leading={
                <span className="text-[13px] font-black uppercase tracking-[0.08em]">S</span>
              }
              trailing={<Arrow />}
            />
            <div className="border-t border-[rgba(26,26,26,0.06)]" />
            <SettingsRow
              label="Юридическая информация"
              description="Реальные юридические документы"
              href={legalHref}
              leading={
                <span className="text-[13px] font-black uppercase tracking-[0.08em]">L</span>
              }
              trailing={<Arrow />}
            />
          </MiniappSurface>
          <PrimaryButton
            tone="ghost"
            onClick={handleLogout}
            className="w-full border-[rgba(232,93,42,0.18)] bg-[rgba(232,93,42,0.08)] text-[rgba(232,93,42,0.92)]"
          >
            Выйти
          </PrimaryButton>
        </div>
      </PageFrame>

      <ProfilePanel
        open={activePanel === 'support'}
        title="Поддержка"
        description="Связаться с поддержкой можно прямо из профиля."
        onClose={() => setActivePanel(null)}
      >
        <div className="space-y-4">
          <p className="text-[14px] leading-6 text-[var(--foreground)]">
            По любым вопросам и с отзывами можете обращаться в нашу поддержку.
          </p>
          <div className="grid grid-cols-1 gap-2">
            <PrimaryButton
              tone="ghost"
              onClick={() => {
                trackEvent('profile_support_telegram_click', { source: 'profile' });
                openExternal(supportUrl);
              }}
            >
              @inhoundsupport
            </PrimaryButton>
            <PrimaryButton
              tone="ghost"
              onClick={() => {
                trackEvent('profile_support_email_click', { source: 'profile' });
                openExternal(supportEmailUrl);
              }}
            >
              info@inhound.club
            </PrimaryButton>
          </div>
        </div>
      </ProfilePanel>

      <ProfilePanel
        open={activePanel === 'edit-name'}
        title="Редактировать имя"
        description="Новое имя сразу отразится в профиле."
        onClose={() => setActivePanel(null)}
      >
        <div className="space-y-3">
          <label className="block space-y-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[rgba(26,26,26,0.46)]">
              Имя в профиле
            </span>
            <input
              value={displayNameDraft}
              onChange={(event) => {
                setDisplayNameDraft(event.target.value);
                setDisplayNameError(null);
              }}
              placeholder="Как вас показывать в приложении"
              className="w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-white px-4 py-3 text-[14px] text-[var(--foreground)] outline-none transition focus:border-[rgba(107,78,230,0.32)]"
            />
          </label>
          {displayNameError ? (
            <p className="text-[12px] leading-5 text-[rgba(232,93,42,0.9)]">{displayNameError}</p>
          ) : null}
          <div className="grid grid-cols-1 gap-2">
            <PrimaryButton onClick={handleSaveDisplayName} disabled={profileMutation.isPending}>
              {profileMutation.isPending ? 'Сохраняем...' : 'Сохранить имя'}
            </PrimaryButton>
            <PrimaryButton tone="ghost" onClick={() => setActivePanel(null)}>
              Отмена
            </PrimaryButton>
          </div>
        </div>
      </ProfilePanel>

      <ProfilePanel
        open={activePanel === 'email-link'}
        title="Добавить email"
        description="Подтвердите email одноразовым кодом. После этого он станет частью аккаунта."
        onClose={() => {
          resetEmailLinkState();
          setActivePanel(null);
        }}
      >
        <div className="space-y-3">
          {emailLinkStep === 'verify' ? (
            <p className="rounded-[16px] bg-[rgba(107,78,230,0.08)] px-4 py-3 text-[13px] leading-5 text-[rgba(26,26,26,0.72)]">
              Код отправлен на {emailLinkSentTo}. Введите его, чтобы привязать email к профилю.
            </p>
          ) : null}
          <label className="block space-y-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[rgba(26,26,26,0.46)]">
              Email
            </span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={emailLinkStep === 'verify' ? emailLinkSentTo : emailLinkEmail}
              onChange={(event) => {
                setEmailLinkEmail(event.target.value);
                setEmailLinkError(null);
              }}
              disabled={emailLinkPending || emailLinkStep === 'verify'}
              placeholder="you@example.com"
              className="w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-white px-4 py-3 text-[14px] text-[var(--foreground)] outline-none transition focus:border-[rgba(107,78,230,0.32)]"
            />
          </label>
          {emailLinkStep === 'verify' ? (
            <label className="block space-y-2">
              <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[rgba(26,26,26,0.46)]">
                Одноразовый код
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={emailLinkCode}
                onChange={(event) => {
                  setEmailLinkCode(event.target.value);
                  setEmailLinkError(null);
                }}
                disabled={emailLinkPending}
                placeholder="123456"
                className="w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-white px-4 py-3 text-[14px] text-[var(--foreground)] outline-none transition focus:border-[rgba(107,78,230,0.32)]"
              />
            </label>
          ) : null}
          {emailLinkError ? (
            emailLinkError === EMAIL_CONFLICT_SUPPORT_MESSAGE ? (
              <EmailConflictSupportMessage />
            ) : (
              <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
                {emailLinkError}
              </p>
            )
          ) : null}
          <div className="grid grid-cols-1 gap-2">
            <PrimaryButton
              onClick={() =>
                void (emailLinkStep === 'request'
                  ? handleRequestEmailLinkCode()
                  : handleVerifyEmailLinkCode())
              }
              disabled={emailLinkPending}
              className="w-full"
            >
              {emailLinkStep === 'request' ? 'Получить код' : 'Привязать email'}
            </PrimaryButton>
            {emailLinkStep === 'verify' ? (
              <PrimaryButton
                tone="ghost"
                onClick={() => {
                  setEmailLinkStep('request');
                  setEmailLinkCode('');
                  setEmailLinkSentTo('');
                  setEmailLinkError(null);
                }}
                className="w-full"
              >
                Изменить email
              </PrimaryButton>
            ) : null}
          </div>
        </div>
      </ProfilePanel>
    </>
  );
}

function ProfilePanel({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(16,12,8,0.32)] backdrop-blur-[2px]">
      <button type="button" aria-label="Закрыть" className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 flex min-h-full items-end justify-center px-3 pb-[calc(var(--safe-area-bottom)+12px)] pt-[max(64px,calc(var(--safe-area-top)+20px))] min-[430px]:px-5">
        <div
          role="dialog"
          aria-modal="true"
          className="relative z-10 w-full max-w-[420px] overflow-hidden rounded-[28px] border border-[rgba(26,26,26,0.08)] bg-[var(--card)] shadow-[0_18px_48px_rgba(26,26,26,0.18)]"
        >
          <div className="max-h-[min(78vh,680px)] overflow-y-auto px-4 pb-4 pt-3 min-[430px]:px-5 min-[430px]:pb-5">
            <div className="mb-4 flex justify-center">
              <div className="h-1.5 w-14 rounded-full bg-[rgba(26,26,26,0.12)]" />
            </div>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <h2 className="text-[18px] font-black uppercase tracking-[0.02em] text-[var(--foreground)]">
                  {title}
                </h2>
                <p className="text-[13px] leading-5 text-[rgba(26,26,26,0.58)]">{description}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-full bg-[rgba(26,26,26,0.06)] px-3 py-1 text-[12px] font-semibold text-[rgba(26,26,26,0.62)]"
              >
                Закрыть
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-4 min-[430px]:px-5">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-[14px] font-semibold text-[var(--foreground)]">{label}</p>
        <p className="text-[12px] leading-5 text-[rgba(26,26,26,0.56)]">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition ${
          checked ? 'bg-[rgba(107,78,230,0.92)]' : 'bg-[rgba(26,26,26,0.16)]'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition ${
            checked ? 'left-6' : 'left-1'
          }`}
        />
      </button>
    </div>
  );
}

function Arrow({ accent = false }: { accent?: boolean }) {
  return (
    <span className={accent ? 'text-[rgba(107,78,230,0.42)]' : 'text-[rgba(26,26,26,0.34)]'}>
      →
    </span>
  );
}
